import { useEffect } from "react";
import { getOGImageUrl } from "./utils/ogImageUrl";

type BreadcrumbItem = {
  name: string;
  url: string;
};

type FAQItem = {
  question: string;
  answer: string;
};

type SeoOptions = {
  title: string;
  description: string;
  path: string;
  image?: string;
  breadcrumbs?: BreadcrumbItem[];
  faqItems?: FAQItem[];
};

const SITE_URL = "https://holaworldmigration.com.au";

const setMetaTag = (
  selector: string,
  createAttributes: Record<string, string>,
  content: string
) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(createAttributes).forEach(([key, value]) => {
      element?.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const setCanonical = (href: string) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const setJsonLdScript = (id: string, data: Record<string, unknown>) => {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

export const useSeo = ({ title, description, path, image, breadcrumbs = [], faqItems = [] }: SeoOptions) => {
  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${path}`;
    const imageUrl = getOGImageUrl();

    document.title = title;

    setMetaTag('meta[name="description"]', { name: "description" }, description);

    setMetaTag('meta[property="og:title"]', { property: "og:title" }, title);
    setMetaTag('meta[property="og:description"]', { property: "og:description" }, description);
    setMetaTag('meta[property="og:image"]', { property: "og:image" }, imageUrl);
    setMetaTag('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
    setMetaTag('meta[property="og:type"]', { property: "og:type" }, "website");

    setMetaTag('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', { name: "twitter:title" }, title);
    setMetaTag('meta[name="twitter:description"]', { name: "twitter:description" }, description);
    setMetaTag('meta[name="twitter:image"]', { name: "twitter:image" }, imageUrl);

    setCanonical(canonicalUrl);

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Hola World Migration",
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
      sameAs: [
        "https://www.facebook.com/share/1NfUfrymZj/",
        "https://www.instagram.com/holaworldaustralia?igsh=YnUwbmU1NWJwNTF3",
        "https://www.linkedin.com/company/hola-world/",
        "https://youtube.com/@teamholaworldenglish?si=lrjGd11Xh2W_Wj4k"
      ]
    };

    setJsonLdScript("org-schema", organizationSchema);

    if (breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: `${SITE_URL}${item.url}`,
        })),
      };
      setJsonLdScript("breadcrumb-schema", breadcrumbSchema);
    }

    if (faqItems.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      };
      setJsonLdScript("faq-schema", faqSchema);
    }
  }, [title, description, path, image, breadcrumbs, faqItems]);
};
